import { Menu, Moon, PanelLeftClose, PanelLeftOpen, Search, Sun, type LucideIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { useCallback, useState, type ReactNode } from 'react'
import { useTheme } from '../lib/theme'
import { cn, safeStorage } from '../lib/utils'
import { Button } from './button'
import { CommandPalette, useCommandHotkey, type CommandGroup } from './command-palette'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Sheet,
  SheetContent,
  SheetTitle,
} from './overlays'
import { Avatar, Badge, Kbd, Tooltip } from './primitives'

export interface NavItem {
  id: string
  label: string
  icon: LucideIcon
  /** Route path; omitted for items that are not built yet. */
  to?: string
  soon?: boolean
  active?: boolean
}

export interface NavGroup {
  label: string
  items: NavItem[]
}

export interface ShellBrand {
  name: string
  logoUrl?: string | null
  subtitle?: string
  /**
   * A full wordmark shown instead of the name when there is room: one image
   * for light surfaces and one for dark. Only the Bixo-owned apps set it; a
   * tenant's own branding never does.
   */
  wordmark?: { light: string; dark: string; width: number; height: number }
}

/** Renders a router link without tying @bixo/ui to a specific router. */
export type RenderLink = (args: {
  item: NavItem
  className: string
  children: ReactNode
  onNavigate?: () => void
}) => ReactNode

interface AppShellProps {
  brand: ShellBrand
  groups: NavGroup[]
  /** Up to four items for the mobile bottom bar; a Menu tab is always added. */
  mobileTabs: NavItem[]
  user: { name: string; email: string }
  userMenu: ReactNode
  renderLink: RenderLink
  commands: CommandGroup[]
  children: ReactNode
}

const COLLAPSE_KEY = 'bixo.sidebar.collapsed'

export function BrandMark({
  brand,
  compact = false,
  onDark = false,
}: {
  brand: ShellBrand
  compact?: boolean
  /** The mark sits on a dark panel whatever the theme (e.g. the sign-in brand panel). */
  onDark?: boolean
}) {
  if (brand.wordmark && !compact) {
    const { light, dark, width, height } = brand.wordmark
    return (
      <div className="flex min-w-0 items-center gap-2.5">
        {onDark ? (
          <img src={dark} alt={brand.name} width={width} height={height} className="h-8 w-auto shrink-0" />
        ) : (
          <>
            <img
              src={light}
              alt={brand.name}
              width={width}
              height={height}
              className="h-8 w-auto shrink-0 dark:hidden"
            />
            <img
              src={dark}
              alt={brand.name}
              width={width}
              height={height}
              className="hidden h-8 w-auto shrink-0 dark:block"
            />
          </>
        )}
        {brand.subtitle && (
          <p className="truncate border-l border-border pl-2.5 text-xs leading-tight text-muted-foreground">
            {brand.subtitle}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="flex min-w-0 items-center gap-2.5">
      {brand.logoUrl ? (
        <img src={brand.logoUrl} alt="" className="size-8 shrink-0 rounded-lg object-contain" />
      ) : (
        <div className="relative grid size-8 shrink-0 place-items-center overflow-hidden rounded-lg bg-linear-to-br from-brand-400 via-brand-600 to-brand-800 text-sm font-bold text-white shadow-glow">
          <span className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgb(255_255_255/0.35),transparent_60%)]" />
          <span className="relative">{brand.name.trim().charAt(0).toUpperCase() || 'B'}</span>
        </div>
      )}
      {!compact && (
        <div className="min-w-0 leading-tight">
          <p className="truncate text-sm font-semibold tracking-tight">{brand.name}</p>
          {brand.subtitle && <p className="truncate text-xs text-muted-foreground">{brand.subtitle}</p>}
        </div>
      )}
    </div>
  )
}

function NavLinkItem({
  item,
  collapsed,
  renderLink,
  onNavigate,
}: {
  item: NavItem
  collapsed: boolean
  renderLink: RenderLink
  onNavigate?: () => void
}) {
  const base = cn(
    'group relative flex h-9 items-center gap-3 rounded-lg px-2.5 text-[13.5px] font-medium outline-none transition-colors',
    'focus-visible:ring-2 focus-visible:ring-ring/60',
    collapsed && 'justify-center px-0',
  )

  const content = (
    <>
      {item.active && (
        <motion.span
          layoutId="nav-active"
          className="absolute inset-0 rounded-lg bg-accent ring-1 ring-brand-500/15"
          transition={{ type: 'spring', stiffness: 500, damping: 38 }}
        />
      )}
      <item.icon
        className={cn(
          'relative size-4.5 shrink-0',
          item.active ? 'text-accent-foreground' : 'text-muted-foreground group-hover:text-foreground',
        )}
      />
      {!collapsed && (
        <>
          <span className={cn('relative flex-1 truncate', item.active && 'text-accent-foreground')}>
            {item.label}
          </span>
          {item.soon && (
            <Badge variant="neutral" className="relative px-1.5 py-0 text-[10px] tracking-wide uppercase">
              Soon
            </Badge>
          )}
        </>
      )}
    </>
  )

  const node =
    item.soon || !item.to ? (
      <div
        aria-disabled="true"
        className={cn(base, 'cursor-not-allowed text-muted-foreground/70 opacity-75')}
        title={collapsed ? undefined : `${item.label} is coming soon`}
      >
        {content}
      </div>
    ) : (
      renderLink({
        item,
        className: cn(
          base,
          item.active
            ? 'text-accent-foreground'
            : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground',
        ),
        children: content,
        ...(onNavigate ? { onNavigate } : {}),
      })
    )

  return collapsed ? (
    <Tooltip content={item.soon ? `${item.label} · Soon` : item.label} side="right">
      <div>{node}</div>
    </Tooltip>
  ) : (
    node
  )
}

function SidebarNav({
  groups,
  collapsed,
  renderLink,
  onNavigate,
}: {
  groups: NavGroup[]
  collapsed: boolean
  renderLink: RenderLink
  onNavigate?: () => void
}) {
  return (
    <nav className="flex flex-col gap-5" aria-label="Main">
      {groups.map((group) => (
        <div key={group.label} className="flex flex-col gap-0.5">
          {collapsed ? (
            <div className="mx-auto mb-1 h-px w-6 bg-border" aria-hidden />
          ) : (
            <p className="mb-1 px-2.5 text-[11px] font-medium tracking-wider text-muted-foreground/80 uppercase">
              {group.label}
            </p>
          )}
          {group.items.map((item) => (
            <NavLinkItem
              key={item.id}
              item={item}
              collapsed={collapsed}
              renderLink={renderLink}
              {...(onNavigate ? { onNavigate } : {})}
            />
          ))}
        </div>
      ))}
    </nav>
  )
}

export function ThemeToggle() {
  const { resolved, toggle } = useTheme()
  return (
    <Button
      variant="ghost"
      size="icon"
      onClick={toggle}
      aria-label={resolved === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {resolved === 'dark' ? <Sun /> : <Moon />}
    </Button>
  )
}

export function AppShell({
  brand,
  groups,
  mobileTabs,
  user,
  userMenu,
  renderLink,
  commands,
  children,
}: AppShellProps) {
  const [collapsed, setCollapsed] = useState(() => safeStorage.get(COLLAPSE_KEY) === '1')
  const [mobileOpen, setMobileOpen] = useState(false)
  const [paletteOpen, setPaletteOpen] = useState(false)

  const togglePalette = useCallback(() => setPaletteOpen((open) => !open), [])
  useCommandHotkey(togglePalette)

  const toggleCollapsed = (): void => {
    setCollapsed((value) => {
      safeStorage.set(COLLAPSE_KEY, value ? '0' : '1')
      return !value
    })
  }

  return (
    <div className="min-h-dvh bg-background">
      {/* Ambient brand glow behind the content. */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-x-0 top-0 -z-0 h-72 bg-[radial-gradient(60%_100%_at_50%_0%,color-mix(in_oklch,var(--color-brand-500)_10%,transparent),transparent)]"
      />

      {/* Desktop sidebar */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-border bg-sidebar backdrop-blur-xl transition-[width] duration-300 ease-(--ease-out-expo) lg:flex',
          collapsed ? 'w-[68px]' : 'w-64',
        )}
      >
        <div className={cn('flex h-16 items-center px-4', collapsed && 'justify-center px-0')}>
          <BrandMark brand={brand} compact={collapsed} />
        </div>
        <div className="flex-1 scrollbar-thin overflow-y-auto px-3 py-3">
          <SidebarNav groups={groups} collapsed={collapsed} renderLink={renderLink} />
        </div>
        <div className={cn('border-t border-border p-3', collapsed && 'flex justify-center')}>
          <Button
            variant="ghost"
            size={collapsed ? 'icon' : 'sm'}
            className={cn(!collapsed && 'w-full justify-start')}
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
            {!collapsed && 'Collapse'}
          </Button>
        </div>
      </aside>

      {/* Mobile sidebar */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[85vw] max-w-72 bg-surface p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <div className="flex h-16 items-center border-b border-border px-4">
            <BrandMark brand={brand} />
          </div>
          <div className="flex-1 overflow-y-auto px-3 py-4">
            <SidebarNav
              groups={groups}
              collapsed={false}
              renderLink={renderLink}
              onNavigate={() => setMobileOpen(false)}
            />
          </div>
        </SheetContent>
      </Sheet>

      <div
        className={cn(
          'relative flex min-h-dvh flex-col transition-[padding] duration-300 ease-(--ease-out-expo)',
          collapsed ? 'lg:pl-[68px]' : 'lg:pl-64',
        )}
      >
        {/* Top bar */}
        <header className="sticky top-0 z-20 flex h-16 items-center gap-3 border-b border-border bg-background/75 px-4 backdrop-blur-xl sm:px-6">
          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu />
          </Button>
          <div className="lg:hidden">
            <BrandMark brand={brand} compact />
          </div>

          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="group ml-auto flex h-9 w-full max-w-sm cursor-pointer items-center gap-2.5 rounded-lg border border-border bg-surface/80 px-3 text-sm text-muted-foreground shadow-soft transition-colors hover:border-brand-500/30 hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring/60 sm:ml-0 lg:max-w-md"
          >
            <Search className="size-4" />
            <span className="hidden flex-1 text-left sm:block">Search or jump to…</span>
            <span className="hidden items-center gap-0.5 sm:flex">
              <Kbd>⌘</Kbd>
              <Kbd>K</Kbd>
            </span>
          </button>

          <div className="flex items-center gap-1 sm:ml-auto">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex cursor-pointer items-center gap-2 rounded-full p-0.5 transition-shadow focus-visible:ring-2 focus-visible:ring-ring/60 sm:rounded-lg sm:py-1 sm:pr-2 sm:pl-1 sm:hover:bg-muted"
                  aria-label="Account menu"
                >
                  <Avatar name={user.name} />
                  <span className="hidden max-w-32 truncate text-sm font-medium md:block">{user.name}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <div className="px-2.5 py-2">
                  <p className="truncate text-sm font-medium">{user.name}</p>
                  <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                </div>
                {userMenu}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="relative flex-1 px-4 pt-6 pb-28 sm:px-6 lg:px-8 lg:pb-10">
          <div className="mx-auto w-full max-w-7xl">{children}</div>
        </main>
      </div>

      {/* Mobile bottom tab bar */}
      <nav
        aria-label="Quick navigation"
        className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/85 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl lg:hidden"
      >
        <div className="mx-auto flex max-w-md items-stretch justify-around px-2">
          {mobileTabs.slice(0, 3).map((item) =>
            item.to && !item.soon ? (
              <div key={item.id} className="flex-1">
                {renderLink({
                  item,
                  className: cn(
                    'flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-medium transition-colors',
                    item.active ? 'text-primary' : 'text-muted-foreground',
                  ),
                  children: (
                    <>
                      <item.icon className="size-5" />
                      {item.label}
                    </>
                  ),
                })}
              </div>
            ) : null,
          )}
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="flex h-16 flex-1 cursor-pointer flex-col items-center justify-center gap-1 text-[11px] font-medium text-muted-foreground"
          >
            <Search className="size-5" />
            Search
          </button>
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            className="flex h-16 flex-1 cursor-pointer flex-col items-center justify-center gap-1 text-[11px] font-medium text-muted-foreground"
          >
            <Menu className="size-5" />
            Menu
          </button>
        </div>
      </nav>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} groups={commands} />
    </div>
  )
}
